// Maps a resolved route to the GA4 page_viewed dimensions. Pathname-prefix
// based (stable across both the _authed and _public route trees, which
// share URL space); page_id comes from the leaf match's params so it is
// the clean route param, not a string-parsed segment.
//
// A route whose URL param is not the entity's id — the member profile is
// addressed by handle — returns `{ pageId }` from its loader, and that
// wins over the param. page_id stays the pseudonymous uuid it has always
// been rather than a name-derived slug, and profile rows stay joinable
// across the handle cutover.
//
// page_title is the same string the navigation writes to `document.title`,
// so the explicit param and gtag's own auto-captured title cannot disagree.
// A loader's `pageTitle` is the entity's title subject; see `page-title.ts`
// for which routes may supply one.

import { pageSubject } from '@/lib/page-title';
import { composeTitle } from '@/lib/seo-text';

export interface PageMetaLoaderData {
  readonly pageId?: string;
  readonly pageTitle?: string;
}

// The `staleReloadMode` of a route whose loader titles the tab from copy an
// editor can change in this session: the resource and learning track pages.
// A revisited route's loader data is stale, and by default the router resolves
// the navigation with the previous visit's data and reloads it behind; the tab
// is titled once per navigation from the data it resolved with
// (`subscribePageViews`), so a title an editor changed since that visit would
// stay off the tab until the visit after. Blocking makes the navigation
// resolve with the reloaded data. Those loaders read the query cache and fetch
// only when it holds nothing current (`loadCurrentForRoute`), so a blocking
// reload is a cache read unless the entity was written since. Every other
// route keeps the router's background reload, so a revisit renders from cache
// at once. The static pages need neither: their title is read from the
// page-seo cache, not from loader data.
export const EDITABLE_TITLE_RELOAD = 'blocking' as const;

export type PageType =
  | 'circle'
  | 'network'
  | 'group'
  | 'event'
  | 'resource'
  | 'profile'
  | 'feed'
  | 'people'
  | 'onboarding'
  | 'settings'
  | 'messages'
  | 'other';

export interface PageMeta {
  readonly page_type: PageType;
  readonly page_id: string | null;
  readonly page_title: string;
}

const PREFIX_TO_TYPE: ReadonlyArray<[prefix: string, type: PageType]> = [
  ['/circles', 'circle'],
  ['/networks', 'network'],
  ['/groups', 'group'],
  ['/events', 'event'],
  ['/resources', 'resource'],
  ['/members', 'profile'],
  ['/feed', 'feed'],
  ['/people', 'people'],
  ['/onboarding', 'onboarding'],
  ['/settings', 'settings'],
  ['/messages', 'messages'],
];

export function resolvePageMeta(
  pathname: string,
  params: Record<string, string>,
  loaderData?: PageMetaLoaderData,
): PageMeta {
  const page_title = composeTitle(loaderData?.pageTitle ?? pageSubject(pathname));
  const match = PREFIX_TO_TYPE.find(
    ([prefix]) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
  if (!match) return { page_type: 'other', page_id: null, page_title };
  return {
    page_type: match[1],
    page_id: loaderData?.pageId ?? params.id ?? null,
    page_title,
  };
}
